import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, toApiError, unwrap } from './client';
import type { MapInfo, SceneState } from './types';

export const keys = {
  me: ['me'] as const,
  providers: ['providers'] as const,
  scenes: (trash: boolean) => ['scenes', { trash }] as const,
  scene: (id: string) => ['scene', id] as const,
  snapshots: (id: string) => ['snapshots', id] as const,
  snapshot: (id: string, sid: string) => ['snapshot', id, sid] as const,
  maps: ['maps'] as const,
};

/** Usuario de la sesión, o null si no hay sesión (401). */
export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        return (await unwrap(api.GET('/api/me'))).user;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useProviders() {
  return useQuery({
    queryKey: keys.providers,
    queryFn: () => unwrap(api.GET('/api/auth/providers')),
    staleTime: Infinity,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) => unwrap(api.POST('/api/auth/login', { body })),
    onSuccess: (data) => qc.setQueryData(keys.me, data.user),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/auth/logout')),
    onSettled: () => {
      qc.setQueryData(keys.me, null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' && q.queryKey[0] !== 'providers' });
    },
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.DELETE('/api/me')),
    onSuccess: () => {
      qc.setQueryData(keys.me, null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' && q.queryKey[0] !== 'providers' });
    },
  });
}

export function useScenes(trash = false) {
  return useQuery({
    queryKey: keys.scenes(trash),
    queryFn: async () =>
      (await unwrap(api.GET('/api/scenes', { params: { query: { trash: trash ? 'true' : 'false' } } }))).scenes,
  });
}

export function useScene(id: string) {
  return useQuery({
    queryKey: keys.scene(id),
    queryFn: () => unwrap(api.GET('/api/scenes/{id}', { params: { path: { id } } })),
    // El editor es la fuente de verdad mientras está abierto: no se recarga por su cuenta.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
  });
}

const invalidateLists = (qc: ReturnType<typeof useQueryClient>) => qc.invalidateQueries({ queryKey: ['scenes'] });

export function useCreateScene() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; state?: SceneState }) => unwrap(api.POST('/api/scenes', { body })),
    onSuccess: (scene) => {
      qc.setQueryData(keys.scene(scene.id), scene);
      void invalidateLists(qc);
    },
  });
}

export function useRenameScene() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      unwrap(api.PATCH('/api/scenes/{id}', { params: { path: { id } }, body: { name } })),
    onSuccess: () => invalidateLists(qc),
  });
}

export function useDuplicateScene() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.POST('/api/scenes/{id}/duplicate', { params: { path: { id } }, body: {} })),
    onSuccess: () => invalidateLists(qc),
  });
}

export function useTrashScene() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE('/api/scenes/{id}', { params: { path: { id } } })),
    onSuccess: () => invalidateLists(qc),
  });
}

export function useRestoreScene() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.POST('/api/scenes/{id}/restore', { params: { path: { id } } })),
    onSuccess: () => invalidateLists(qc),
  });
}

export function useDeleteSceneForever() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE('/api/scenes/{id}/permanent', { params: { path: { id } } })),
    onSuccess: () => invalidateLists(qc),
  });
}

export function useSnapshots(id: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.snapshots(id),
    queryFn: async () => (await unwrap(api.GET('/api/scenes/{id}/snapshots', { params: { path: { id } } }))).snapshots,
    enabled,
  });
}

export function useSnapshot(id: string, snapshotId: string | null) {
  return useQuery({
    queryKey: keys.snapshot(id, snapshotId ?? ''),
    queryFn: () =>
      unwrap(
        api.GET('/api/scenes/{id}/snapshots/{snapshotId}', { params: { path: { id, snapshotId: snapshotId ?? '' } } }),
      ),
    enabled: snapshotId !== null,
    staleTime: Infinity,
  });
}

export function useCreateSnapshot(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label: string) =>
      unwrap(api.POST('/api/scenes/{id}/snapshots', { params: { path: { id } }, body: { label } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.snapshots(id) }),
  });
}

export function useRestoreSnapshot(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (snapshotId: string) =>
      unwrap(api.POST('/api/scenes/{id}/snapshots/{snapshotId}/restore', { params: { path: { id, snapshotId } } })),
    onSuccess: (scene) => {
      qc.setQueryData(keys.scene(id), scene);
      void qc.invalidateQueries({ queryKey: keys.snapshots(id) });
    },
  });
}

export function useMaps(enabled: boolean) {
  return useQuery({
    queryKey: keys.maps,
    queryFn: async () => (await unwrap(api.GET('/api/maps'))).maps,
    enabled,
  });
}

export interface MapUploadInput {
  file: Blob & { name?: string };
  thumb: Blob | null;
  gridCols?: number;
  gridRows?: number;
}

/** Sube un mapa (multipart). openapi-fetch no tipa multipart, así que va con fetch. */
export function useUploadMap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, thumb, gridCols, gridRows }: MapUploadInput) => {
      const form = new FormData();
      form.append('file', file, file.name ?? 'mapa.webp');
      if (thumb) form.append('thumb', thumb, 'thumb.webp');
      if (gridCols && gridRows) {
        form.append('gridCols', String(gridCols));
        form.append('gridRows', String(gridRows));
      }
      const res = await fetch('/api/maps', { method: 'POST', body: form, credentials: 'same-origin' });
      const body: unknown = await res.json().catch(() => null);
      if (!res.ok) throw toApiError(res.status, body);
      return body as MapInfo;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.maps }),
  });
}

export function useDeleteMap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE('/api/maps/{id}', { params: { path: { id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.maps }),
  });
}
